import React, { useState, useEffect } from 'react';
import AceEditor from 'react-ace';
import { LuArrowDown, LuArrowUp, LuChevronRight, LuPlus, LuSave, LuSettings2, LuTrash2 } from 'react-icons/lu';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import { API_BASE_URL } from '../../config.js';
import { cx } from '../../utils/cx.js';
import { slotOf } from '../../utils/reportPeriod.js';
import { compareReportsDesc, formatSlot } from '../../utils/weekFormat.js';
import { sourceLabel } from '../../utils/sources.js';
import { useSchedulePreview } from '../../hooks/useSchedulePreview.js';
import { Modal } from '../ui/Modal';
import Alert from '../ui/Alert';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Field, { INPUT_CLASS } from '../ui/Field';
import IconButton from '../ui/IconButton';
import SegmentedControl from '../ui/SegmentedControl';
import Toggle from '../ui/Toggle';
import ConfirmModal from '../ui/ConfirmModal';
import SourceSelector from './SourcesSelector';
import ScheduleCard from './ScheduleCard';
import ScheduleShiftDialog from './ScheduleShiftDialog';

const isDarkTheme = () => document.documentElement.classList.contains('dark');

const aceProps = {
  mode: 'json',
  editorProps: { $blockScrolling: true },
  width: '100%',
  fontSize: 13,
  wrapEnabled: true,
};

/** Edition of a client configuration: general settings, source (read only), analyses. */
const ConfigDetailModal = ({ config, onClose, onSave, isLoading, fetchWithAuth }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [analysisItems, setAnalysisItems] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [generalData, setGeneralData] = useState({
    slug: '',
    name: '',
    public_sharing_enabled: false,
    is_source_active: true,
    report_day: 1,
    report_hour: 0,
  });
  const [expandedItems, setExpandedItems] = useState({});
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Confirmation when the new slot pushes the next report later: { pending, error } while open.
  const [shift, setShift] = useState(null);

  // The source selector rewrites 'all' as the explicit list on first display: both mean the same.
  const expandAllSources = (sources) => {
    if (sources !== 'all' || !config?.source_config?.workspaces) return sources;
    return {
      workspaces: Object.fromEntries(Object.entries(config.source_config.workspaces)
        .map(([name, workspace]) => [name, { channels: [...(workspace.channels || [])] }])),
    };
  };
  const snapshotOf = (general, items) => JSON.stringify({
    general,
    items: items.map((item) => ({ ...item, sources: expandAllSources(item.sources) })),
  });

  // Initialize form data from config
  useEffect(() => {
    if (!config) return;
    const general = {
      slug: config.slug || '',
      name: config.name || '',
      public_sharing_enabled: config.public_sharing_enabled || false,
      is_source_active: config.is_source_active !== false,
      report_day: slotOf(config).day,
      report_hour: slotOf(config).hour,
    };
    setGeneralData(general);

    if (config.weekly_config && config.weekly_config.analysis) {
      // Migration from the old format (type -> slug, missing name)
      const migratedItems = config.weekly_config.analysis.map((item) => {
        const newItem = { ...item };
        if (item.type && !item.slug) {
          newItem.slug = item.type;
          delete newItem.type;
        }
        if (!newItem.name && newItem.slug) {
          newItem.name = newItem.slug.charAt(0).toUpperCase() + newItem.slug.slice(1);
        }
        // Empty strings rather than undefined: the inputs stay controlled.
        newItem.slug = newItem.slug || '';
        newItem.name = newItem.name || '';
        newItem.prompt = newItem.prompt || '';
        newItem.sources = newItem.sources || 'all';
        return newItem;
      });
      setAnalysisItems([...migratedItems]);
      setSavedSnapshot(snapshotOf(general, migratedItems));
    } else {
      setAnalysisItems([]);
      setSavedSnapshot(snapshotOf(general, []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Keep the JSON view in sync with the form
  useEffect(() => {
    setJsonValue(JSON.stringify({ analysis: analysisItems }, null, 2));
  }, [analysisItems]);

  useEffect(() => {
    if (!saveSuccess) return undefined;
    const t = setTimeout(() => setSaveSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [saveSuccess]);

  // Parse the JSON editor; null (and an error shown) when it is not a valid { analysis: [...] }.
  const parseJsonItems = () => {
    try {
      const parsed = JSON.parse(jsonValue);
      if (parsed && Array.isArray(parsed.analysis)) {
        setJsonError(null);
        return parsed.analysis;
      }
      setJsonError('Structure JSON invalide : { "analysis": [...] } attendu.');
    } catch (error) {
      setJsonError(`JSON invalide : ${error.message}`);
    }
    return null;
  };

  const setEditMode = (mode) => {
    if (mode === 'json' && !jsonMode) {
      setJsonValue(JSON.stringify({ analysis: analysisItems }, null, 2));
      setJsonMode(true);
    } else if (mode === 'form' && jsonMode) {
      // An invalid JSON keeps the editor open: switching would silently drop the edits.
      const items = parseJsonItems();
      if (!items) return;
      setAnalysisItems(items);
      setJsonMode(false);
    }
  };

  const handleJsonChange = (value) => {
    setJsonValue(value);
    setJsonError(null);
  };

  const handleAddItem = () => {
    const newIndex = analysisItems.length;
    setAnalysisItems([...analysisItems, { slug: '', name: '', prompt: '' }]);
    setExpandedItems((prev) => ({ ...prev, [newIndex]: true }));
  };

  const handleRemoveItem = (index) => {
    const newItems = [...analysisItems];
    newItems.splice(index, 1);
    setAnalysisItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...analysisItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setAnalysisItems(newItems);
  };

  // The slug follows the name until it has been customized.
  const handleNameChange = (index, value) => {
    const newItems = [...analysisItems];
    const current = newItems[index];
    const autoSlug = !current.slug || current.slug === current.name?.toLowerCase().replace(/\s+/g, '-');
    newItems[index] = { ...current, name: value, slug: autoSlug ? value.toLowerCase().replace(/\s+/g, '-') : current.slug };
    setAnalysisItems(newItems);
  };

  const handleGeneralChange = (field, value) => setGeneralData({ ...generalData, [field]: value });

  const toggleItemExpanded = (index) => setExpandedItems((prev) => ({ ...prev, [index]: !prev[index] }));

  const handleMove = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= analysisItems.length) return;
    const newItems = [...analysisItems];
    [newItems[index], newItems[target]] = [newItems[target], newItems[index]];
    setAnalysisItems(newItems);
  };

  const handleSourcesChange = (index, value) => {
    const newItems = [...analysisItems];
    newItems[index] = { ...newItems[index], sources: value };
    setAnalysisItems([...newItems]);
  };

  const persist = async (weeklyConfig, items = analysisItems) => {
    const result = await onSave(config.id, {
      slug: generalData.slug,
      public_sharing_enabled: generalData.public_sharing_enabled,
      is_source_active: generalData.is_source_active,
      report_day: generalData.report_day,
      report_hour: generalData.report_hour,
      weekly_config: weeklyConfig,
    });
    if (result.success) {
      setSaveSuccess(true);
      setSavedSnapshot(snapshotOf(generalData, items));
    } else {
      setSaveError(result.error || 'L\'enregistrement a échoué.');
    }
    return result;
  };

  const savedSlot = slotOf(config);
  const scheduleDirty = generalData.report_day !== savedSlot.day || generalData.report_hour !== savedSlot.hour;
  const preview = useSchedulePreview({ fetchWithAuth, configId: config.id, day: generalData.report_day, hour: generalData.report_hour });
  // The « pushed » warning needs the preview of the new slot before saving.
  const previewPending = scheduleDirty && preview.status === 'loading';

  // Latest weekly, for what « Enregistrer et générer maintenant » will do; undefined = unknown.
  const [latestWeekly, setLatestWeekly] = useState(undefined);
  const shiftOpen = !!shift;
  useEffect(() => {
    if (!shiftOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${config.slug}/weeks`);
        const data = await response.json().catch(() => null);
        if (!cancelled) setLatestWeekly(response.ok && Array.isArray(data) ? [...data].sort(compareReportsDesc)[0] ?? null : undefined);
      } catch {
        if (!cancelled) setLatestWeekly(undefined);
      }
    })();
    return () => { cancelled = true; };
  }, [shiftOpen, fetchWithAuth, config.slug]);

  // Escape, a click outside or "Fermer" never drop edits silently.
  const dirty = savedSnapshot !== null && (
    snapshotOf(generalData, analysisItems) !== savedSnapshot
    || (jsonMode && jsonValue !== JSON.stringify({ analysis: analysisItems }, null, 2))
  );
  const requestClose = () => {
    if (isLoading) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const handleSave = async ({ confirmed = false } = {}) => {
    setSaveError(null);
    setSaveSuccess(false);

    if (!/^[a-z0-9-]+$/.test(generalData.slug)) {
      const error = 'Le slug ne peut contenir que des minuscules, des chiffres et des tirets.';
      setSaveError(error);
      return { success: false, error };
    }

    if (!confirmed && scheduleDirty && preview.status === 'ready' && preview.data.pushed) {
      setShift({ pending: null, error: null });
      return { success: false };
    }

    try {
      if (activeTab !== 'weekly') return await persist(config.weekly_config);

      // Saving from the JSON view saves what is typed there.
      let items = analysisItems;
      if (jsonMode) {
        items = parseJsonItems();
        if (!items) return { success: false };
        setAnalysisItems(items);
      }

      if (items.some((item) => !item.slug?.trim() || !item.name?.trim() || !item.prompt?.trim())) {
        setSaveError('Chaque analyse doit avoir un nom, un slug et un prompt.');
        return { success: false };
      }

      // Keep the other weekly_config blocks (e.g. gallery) untouched
      return await persist({
        ...config.weekly_config,
        analysis: items.map(({ slug, name, prompt, sources }) => (
          sources !== undefined ? { slug, name, prompt, sources } : { slug, name, prompt }
        )),
      }, items);
    } catch (error) {
      setSaveError(`Erreur : ${error.message}`);
      return { success: false, error: `Erreur : ${error.message}` };
    }
  };

  // From the shift dialog: save, then optionally start a snapshot of the new period.
  const confirmShift = async (generate) => {
    setShift({ pending: generate ? 'generate' : 'save', error: null });
    const result = await handleSave({ confirmed: true });
    if (!result?.success) {
      setShift({ pending: null, error: result?.error || 'L\'enregistrement a échoué.' });
      return;
    }
    if (generate) {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${generalData.slug}/generate-now`, { method: 'POST' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          setShift({ pending: null, error: `Configuration enregistrée, mais la génération a échoué : ${data.message || 'erreur inconnue'}` });
          return;
        }
      } catch (error) {
        setShift({ pending: null, error: `Configuration enregistrée, mais la génération a échoué : ${error.message}` });
        return;
      }
    }
    setShift(null);
  };

  const canSave = activeTab !== 'source';

  return (
    <Modal
      isOpen
      onClose={requestClose}
      dismissible={!isLoading}
      icon={LuSettings2}
      title={config.name}
      subtitle={`Configuration du client · ${sourceLabel(config.source_type)} · ${config.slug}`}
      size="lg"
      footer={(
        <>
          <div className="mr-auto min-w-0 text-sm" aria-live="polite">
            {saveError && <span className="text-red-600 dark:text-red-400">{saveError}</span>}
            {saveSuccess && <span className="text-green-700 dark:text-green-400">Configuration enregistrée.</span>}
            {!saveError && !saveSuccess && scheduleDirty && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {previewPending ? 'Calcul du prochain rapport…' : `Nouveau créneau : ${formatSlot(generalData.report_day, generalData.report_hour)}`}
              </span>
            )}
          </div>
          <Button onClick={requestClose} disabled={isLoading}>Fermer</Button>
          {canSave && <Button variant="primary" icon={LuSave} onClick={() => handleSave()} loading={isLoading} disabled={previewPending}>Enregistrer</Button>}
        </>
      )}
    >
      <div className="space-y-5">
        <SegmentedControl
          ariaLabel="Sections de la configuration"
          value={activeTab}
          onChange={(tab) => { setActiveTab(tab); setSaveError(null); }}
          options={[
            { value: 'general', label: 'Général' },
            { value: 'source', label: 'Source' },
            { value: 'weekly', label: 'Analyses', count: analysisItems.length },
          ]}
        />

        {activeTab === 'general' && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nom" htmlFor="config-name" hint="Modifiable uniquement via l'outil en ligne de commande.">
                <input id="config-name" type="text" value={generalData.name} disabled className={INPUT_CLASS} />
              </Field>
              <Field label="Type de source" htmlFor="config-source" hint="Modifiable uniquement via l'outil en ligne de commande.">
                <input id="config-source" type="text" value={config.source_type} disabled className={INPUT_CLASS} />
              </Field>
            </div>
            <Field label="Slug" htmlFor="config-slug" hint="Utilisé dans les adresses (/w/slug) : minuscules, chiffres et tirets uniquement.">
              <input
                id="config-slug"
                type="text"
                value={generalData.slug}
                onChange={(e) => handleGeneralChange('slug', e.target.value)}
                placeholder="ex. : mon-client"
                className={cx(INPUT_CLASS, 'font-mono')}
              />
            </Field>
            <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-900 dark:border-gray-800">
              <Toggle
                className="p-4"
                label="Partage public"
                description="Les rapports publiés peuvent être partagés par lien avec des personnes sans compte."
                checked={generalData.public_sharing_enabled}
                onChange={(value) => handleGeneralChange('public_sharing_enabled', value)}
              />
              <Toggle
                className="p-4"
                label="Source active"
                description="Désactivée, la source n'est plus récupérée par le cron hebdomadaire. Les rapports existants restent consultables."
                checked={generalData.is_source_active}
                onChange={(value) => handleGeneralChange('is_source_active', value)}
              />
            </div>
            <ScheduleCard
              day={generalData.report_day}
              hour={generalData.report_hour}
              dirty={scheduleDirty}
              preview={preview}
              disabled={isLoading}
              onChange={handleGeneralChange}
            />
          </div>
        )}

        {activeTab === 'source' && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge tone="slate" size="sm">Type : {config.source_type}</Badge>
              <Badge tone="slate" size="sm" className="font-mono">{config.slug}</Badge>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
              <AceEditor
                {...aceProps}
                theme={isDarkTheme() ? 'monokai' : 'github'}
                value={JSON.stringify(config.source_config, null, 2)}
                name="source-config-editor"
                setOptions={{ useWorker: false, showLineNumbers: true, tabSize: 2, readOnly: true, highlightActiveLine: false, showPrintMargin: false }}
                height="320px"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Lecture seule : la configuration de la source se modifie via l'outil en ligne de commande. Les secrets sont masqués.
            </p>
          </div>
        )}

        {activeTab === 'weekly' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Analyses produites chaque semaine, dans l'ordre d'affichage.</p>
              <SegmentedControl
                size="sm"
                ariaLabel="Mode d'édition"
                value={jsonMode ? 'json' : 'form'}
                onChange={setEditMode}
                options={[{ value: 'form', label: 'Formulaire' }, { value: 'json', label: 'JSON' }]}
              />
            </div>

            {jsonError && <Alert tone="danger">{jsonError}</Alert>}

            {jsonMode ? (
              <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
                <AceEditor
                  {...aceProps}
                  theme={isDarkTheme() ? 'monokai' : 'github'}
                  value={jsonValue}
                  onChange={handleJsonChange}
                  name="analysis-json-editor"
                  setOptions={{ useWorker: false, showLineNumbers: true, tabSize: 2, highlightActiveLine: true, showPrintMargin: false }}
                  height="420px"
                />
              </div>
            ) : (
              <div className="space-y-3">
                {analysisItems.map((item, index) => {
                  const expanded = !!expandedItems[index];
                  return (
                    <div key={index} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center gap-2 bg-gray-50/80 px-3 py-2 dark:bg-night-850">
                        <button
                          type="button"
                          onClick={() => toggleItemExpanded(index)}
                          aria-expanded={expanded}
                          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-lg py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                        >
                          <LuChevronRight className={cx('h-4 w-4 flex-shrink-0 text-gray-400 transition-transform', expanded && 'rotate-90')} aria-hidden />
                          <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-brand-50 text-xs font-bold text-brand-600 dark:bg-brand-900 dark:text-brand-300">{index + 1}</span>
                          <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">{item.name || 'Nouvelle analyse'}</span>
                          {item.slug && <Badge tone="slate" size="sm" className="hidden font-mono sm:inline-flex">{item.slug}</Badge>}
                        </button>
                        <IconButton icon={LuArrowUp} size="xs" label="Monter" onClick={() => handleMove(index, -1)} disabled={index === 0} />
                        <IconButton icon={LuArrowDown} size="xs" label="Descendre" onClick={() => handleMove(index, 1)} disabled={index === analysisItems.length - 1} />
                        <IconButton icon={LuTrash2} size="xs" variant="danger" label="Retirer cette analyse" onClick={() => handleRemoveItem(index)} />
                      </div>

                      {expanded && (
                        <div className="space-y-4 p-4">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Nom affiché" htmlFor={`analysis-name-${index}`}>
                              <input
                                id={`analysis-name-${index}`}
                                type="text"
                                value={item.name}
                                onChange={(e) => handleNameChange(index, e.target.value)}
                                placeholder="ex. : Synthèse générale"
                                className={INPUT_CLASS}
                              />
                            </Field>
                            <Field label="Slug (identifiant)" htmlFor={`analysis-slug-${index}`} hint="Généré depuis le nom, personnalisable.">
                              <input
                                id={`analysis-slug-${index}`}
                                type="text"
                                value={item.slug}
                                onChange={(e) => handleItemChange(index, 'slug', e.target.value)}
                                placeholder="ex. : general"
                                className={cx(INPUT_CLASS, 'font-mono')}
                              />
                            </Field>
                          </div>
                          <Field label="Prompt" htmlFor={`analysis-prompt-${index}`}>
                            <textarea
                              id={`analysis-prompt-${index}`}
                              value={item.prompt}
                              onChange={(e) => handleItemChange(index, 'prompt', e.target.value)}
                              rows={8}
                              placeholder="Consignes données à l'IA pour cette analyse…"
                              className={cx(INPUT_CLASS, 'resize-y leading-relaxed')}
                            />
                          </Field>
                          {config.source_type === 'slack' && (
                            <Field label="Sources (espaces de travail et canaux)">
                              <SourceSelector
                                value={item.sources || 'all'}
                                onChange={(value) => handleSourcesChange(index, value)}
                                sourceConfig={config.source_config}
                                itemId={index}
                              />
                            </Field>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={handleAddItem}
                  className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 p-3 text-sm font-medium text-gray-500 outline-none transition-colors hover:border-brand-300 hover:bg-brand-50/50 hover:text-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-700 dark:text-gray-400 dark:hover:border-brand-700 dark:hover:bg-brand-950 dark:hover:text-brand-300"
                >
                  <LuPlus className="h-4 w-4" aria-hidden />
                  Ajouter une analyse
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ScheduleShiftDialog
        isOpen={!!shift}
        preview={preview.data}
        latestWeekly={latestWeekly}
        savedSlot={savedSlot}
        newSlot={{ day: generalData.report_day, hour: generalData.report_hour }}
        pending={shift?.pending}
        error={shift?.error}
        onCancel={() => setShift(null)}
        onSave={() => confirmShift(false)}
        onSaveAndGenerate={() => confirmShift(true)}
      />

      <ConfirmModal
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        title="Fermer sans enregistrer ?"
        message="Les modifications de cette configuration n'ont pas été enregistrées : elles seront perdues."
        confirmLabel="Fermer sans enregistrer"
        cancelLabel="Continuer l'édition"
      />
    </Modal>
  );
};

export default ConfigDetailModal;
